import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Autocomplete,
  Chip,
  CircularProgress,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import moment from "moment/min/moment-with-locales";
import MDButton from "../../components/MDButton";
import { propertyAPI } from "../../services/api/property";
import { tenantAPI } from "../../services/api/tenant";
import { artisanAPI } from "../../services/api/artisan";

// Ensure locale is set
moment.locale("de");

const EventDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  initialData,
  availableUsernames,
  isEditMode,
}) => {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [artisans, setArtisans] = useState([]);

  useEffect(() => {
    if (open) {
      if (properties.length === 0) {
        propertyAPI
          .getProperties({ limit: 1000 })
          .then((res) => setProperties(res.data));
      }
      if (tenants.length === 0) {
        tenantAPI
          .getTenants({ limit: 1000 })
          .then((res) => setTenants(res.data));
      }
      if (artisans.length === 0) {
        artisanAPI
          .getArtisans({ limit: 1000 })
          .then((res) => setArtisans(res.data));
      }
    }
  }, [open]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start: new Date(),
    end: new Date(),
    invitees: "",
    notifications: [{ days: 0, hours: 1, minutes: 0 }],
    task_id: null,
    email_message_id: null,
    color: "#3174ad",
    property_id: null,
    tenant_id: null,
    artisan_id: null,
  });

  // Reset form when opening or initialData changes
  useEffect(() => {
    if (open && initialData) {
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        start: initialData.start || new Date(),
        end: initialData.end || new Date(),
        invitees: initialData.invitees || "",
        notifications: initialData.notifications || [
          { days: 0, hours: 1, minutes: 0 },
        ],
        task_id: initialData.task_id || initialData.taskId || null,
        email_message_id: initialData.email_message_id || null,
        color: initialData.color || "#3174ad",
        property_id: initialData.property_id || null,
        tenant_id: initialData.tenant_id || null,
        artisan_id: initialData.artisan_id || null,
      });
    }
  }, [open, initialData]);

  const handleSaveInternal = async () => {
    setLoading(true);
    try {
      await onSave(formData);
      // Don't close here, allow parent to close or handle errors
    } catch (error) {
      console.error("Error saving event:", error);
    } finally {
      setLoading(false);
    }
  };

  const addNotification = () => {
    setFormData({
      ...formData,
      notifications: [
        ...formData.notifications,
        { days: 0, hours: 1, minutes: 0 },
      ],
    });
  };

  const removeNotification = (index) => {
    setFormData({
      ...formData,
      notifications: formData.notifications.filter((_, i) => i !== index),
    });
  };

  const updateNotification = (index, field, value) => {
    const updatedNotifications = formData.notifications.map((notif, i) =>
      i === index ? { ...notif, [field]: parseInt(value) || 0 } : notif,
    );
    setFormData({
      ...formData,
      notifications: updatedNotifications,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? "Termin bearbeiten" : "Neuen Termin erstellen"}
      </DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          {formData.email_message_id && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Verknüpfte E-Mail:{" "}
                <Link
                  component={RouterLink}
                  to={`/emails?id=${formData.email_message_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  E-Mail öffnen (ID: {formData.email_message_id})
                </Link>
              </Typography>
            </Box>
          )}

          {isEditMode && formData.task_id && (
            <Box mb={2}>
              <Button
                component={RouterLink}
                to={`/dashboard?task=${formData.task_id}`}
                variant="outlined"
                color="primary"
                fullWidth
              >
                Zur zugehörigen Aufgabe wechseln
              </Button>
            </Box>
          )}

          <TextField
            fullWidth
            label="Termin Titel"
            margin="normal"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            slotProps={{
              input: {
                lang: "de",
                spellCheck: "true",
                autoCorrect: "on",
              },
            }}
          />
          <TextField
            fullWidth
            label="Beschreibung"
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            slotProps={{
              input: {
                lang: "de",
                spellCheck: "true",
                autoCorrect: "on",
              },
            }}
          />
          <Box display="flex" gap={2} mt={2}>
            <TextField
              label="Farbe"
              type="color"
              value={formData.color}
              onChange={(e) =>
                setFormData({ ...formData, color: e.target.value })
              }
              fullWidth
              sx={{ width: "150px" }}
            />
          </Box>
          <Box display="flex" gap={2} mt={2} mb={2}>
            <Autocomplete
              fullWidth
              options={properties}
              getOptionLabel={(p) =>
                p ? `${p.address} (${p.property_number}), ${p.city}` : ""
              }
              filterOptions={(options, { inputValue }) => {
                const input = (inputValue || "").trim().toLowerCase();
                if (!input) return options;
                return options.filter((opt) => {
                  const combined = `${opt.address || ""} ${
                    opt.property_number || ""
                  } ${opt.city || ""}`.toLowerCase();
                  return combined.includes(input);
                });
              }}
              value={
                properties.find((p) => p.id === formData.property_id) || null
              }
              onChange={(e, val) => {
                const updates = { property_id: val ? val.id : null };
                if (val && !formData.tenant_id) {
                  const firstTenantId =
                    val.tenant_ids?.[0] || val.tenants?.[0]?.id;
                  if (firstTenantId) {
                    updates.tenant_id = firstTenantId;
                  } else {
                    const relatedTenant = tenants.find(
                      (t) =>
                        t.property_ids?.includes(val.id) ||
                        t.properties?.some((p) => p.id === val.id),
                    );
                    if (relatedTenant) updates.tenant_id = relatedTenant.id;
                  }
                }
                setFormData({ ...formData, ...updates });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Immobilie verknüpfen" />
              )}
            />
            <Autocomplete
              fullWidth
              options={tenants}
              getOptionLabel={(t) => (t ? `${t.name}` : "")}
              value={tenants.find((t) => t.id === formData.tenant_id) || null}
              onChange={(e, val) => {
                const updates = { tenant_id: val ? val.id : null };
                if (val && !formData.property_id) {
                  const firstPropId =
                    val.property_ids?.[0] || val.properties?.[0]?.id;
                  if (firstPropId) {
                    updates.property_id = firstPropId;
                  } else {
                    const relatedProp = properties.find(
                      (p) =>
                        p.tenant_ids?.includes(val.id) ||
                        p.tenants?.some((t) => t.id === val.id),
                    );
                    if (relatedProp) updates.property_id = relatedProp.id;
                  }
                }
                setFormData({ ...formData, ...updates });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Mieter verknüpfen" />
              )}
            />
            <Autocomplete
              fullWidth
              options={artisans}
              getOptionLabel={(a) =>
                a ? `${a.company_name} (${a.trade})` : ""
              }
              value={artisans.find((a) => a.id === formData.artisan_id) || null}
              onChange={(e, val) =>
                setFormData({ ...formData, artisan_id: val ? val.id : null })
              }
              renderInput={(params) => (
                <TextField {...params} label="Handwerker verknüpfen" />
              )}
            />
          </Box>
          <Autocomplete
            multiple
            freeSolo
            options={availableUsernames}
            value={
              formData.invitees
                ? formData.invitees
                    .split(",")
                    .map((e) => e.trim())
                    .filter(Boolean)
                : []
            }
            onChange={(event, newValue) => {
              setFormData({ ...formData, invitees: newValue.join(", ") });
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    variant="outlined"
                    label={option}
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Personen einladen"
                margin="normal"
                placeholder="Personen hinzufügen"
                helperText="Geben Sie Personen ein oder wählen Sie aus der Liste."
              />
            )}
          />
          <Box display="flex" gap={2} mt={2}>
            <TextField
              fullWidth
              label="Start Datum"
              type="date"
              value={moment(formData.start).format("YYYY-MM-DD")}
              onChange={(e) => {
                const newDate = e.target.value;
                const newStart = new Date(
                  newDate + "T" + moment(formData.start).format("HH:mm"),
                );
                const newEnd = new Date(
                  newDate + "T" + moment(formData.end).format("HH:mm"),
                );
                setFormData({
                  ...formData,
                  start: newStart,
                  end: newEnd,
                });
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Start Zeit"
              type="time"
              value={moment(formData.start).format("HH:mm")}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  start: new Date(
                    moment(formData.start).format("YYYY-MM-DD") +
                      "T" +
                      e.target.value,
                  ),
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
            />
          </Box>
          <Box display="flex" gap={2} mt={2}>
            <TextField
              fullWidth
              label="End Datum"
              type="date"
              value={moment(formData.end).format("YYYY-MM-DD")}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  end: new Date(
                    e.target.value + "T" + moment(formData.end).format("HH:mm"),
                  ),
                })
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="End Zeit"
              type="time"
              value={moment(formData.end).format("HH:mm")}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  end: new Date(
                    moment(formData.end).format("YYYY-MM-DD") +
                      "T" +
                      e.target.value,
                  ),
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Benachrichtigungen
          </Typography>
          {formData.notifications.map((notif, index) => (
            <Box key={index} display="flex" gap={2} alignItems="center" mb={1}>
              <TextField
                label="Tage"
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                value={notif.days}
                onChange={(e) =>
                  updateNotification(index, "days", e.target.value)
                }
                sx={{ flex: 1 }}
              />
              <TextField
                label="Zeit (Stunde:Minute)"
                type="time"
                value={`${String(notif.hours).padStart(2, "0")}:${String(
                  notif.minutes,
                ).padStart(2, "0")}`}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value
                    .split(":")
                    .map(Number);
                  updateNotification(index, "hours", hours);
                  updateNotification(index, "minutes", minutes);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 900 }}
                sx={{ flex: 1 }}
              />
              <Button
                onClick={() => removeNotification(index)}
                color="error"
                variant="outlined"
              >
                Entfernen
              </Button>
            </Box>
          ))}
          <Button onClick={addNotification} variant="outlined" sx={{ mt: 1 }}>
            Benachrichtigung hinzufügen
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        {isEditMode && onDelete && (
          <Button onClick={onDelete} color="error">
            Löschen
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <MDButton
          onClick={handleSaveInternal}
          variant="contained"
          color="info"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Termin speichern"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default EventDialog;
